/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import stride2offset from '@stdlib/strided/base/stride2offset';
import ndarray from './ndarray.js';


// MAIN //

/**
* Computes the maximum absolute value of a single-precision floating-point strided array according to a mask.
*
* @param {PositiveInteger} N - number of indexed elements
* @param {Float32Array} x - input array
* @param {integer} strideX - `x` stride length
* @param {Uint8Array} mask - mask array
* @param {integer} strideMask - `mask` stride length
* @returns {number} maximum absolute value
*
* @example
* import Float32Array from '@stdlib/array/float32';
* import Uint8Array from '@stdlib/array/uint8';
*
* var x = new Float32Array( [ 1.0, -2.0, 4.0, 2.0 ] );
* var mask = new Uint8Array( [ 0, 0, 1, 0 ] );
*
* var v = smskmaxabs( x.length, x, 1, mask, 1 );
* // returns 2.0
*/
function smskmaxabs( N, x, strideX, mask, strideMask ) {
	var ox = stride2offset( N, strideX );
	var om = stride2offset( N, strideMask );
	return ndarray( N, x, strideX, ox, mask, strideMask, om );
}


// EXPORTS //

export default smskmaxabs;
